import { useQuery } from '@apollo/client';
import CircularProgress from 'components/CircularProgress';
import { isEmpty } from 'lodash';
import React, { useMemo } from 'react';
import { useParams } from 'react-router';
import AccountingPageContainer from 'routes/Accountings/components/AccountingPageContainer';
import { GET_COMPANY_BY_INVOICE } from 'services/graphql/queries/company.query';
import { GET_NOTARY_USERS_BY_INVOICE } from 'services/graphql/queries/user.query';
import { ICompany } from 'types/company';
import { IUser } from 'types/user';

import ClientInfoSummary from './components/ClientInfoSummary';
import ClientInvoiceTable from './components/ClientInvoiceTable';
import Filter from './components/Filter';
import NotaryInfoSummary from './components/NotaryInfoSummary';
import NotaryPaymentTable from './components/NotaryPaymentTable';

const AccountingDetail: React.FC = () => {
  const params: Record<string, string> = useParams();
  const { id: targetId, type } = params || {};

  const { data: notaryFromQuery, refetch: refetchNotary, loading: notaryLoading } = useQuery<{
    findInvoiceUserEntity: { data: IUser[] };
  }>(GET_NOTARY_USERS_BY_INVOICE, {
    variables: { id: targetId },
    skip: type !== 'notary',
  });
  const { data: companyFromQuery, refetch: refetchCompany, loading: companyLoading } = useQuery<{
    findInvoiceCompanyEntity: { data: ICompany[] };
  }>(GET_COMPANY_BY_INVOICE, { variables: { id: targetId }, skip: type !== 'company' });

  const notaryData = useMemo(() => notaryFromQuery?.findInvoiceUserEntity?.data?.[0], [notaryFromQuery]);
  const companyData = useMemo(() => companyFromQuery?.findInvoiceCompanyEntity?.data?.[0], [companyFromQuery]);

  const loading = notaryLoading || companyLoading;

  const refetchDataWhenGenerateInvoice = () => {
    if (notaryData && !isEmpty(notaryData)) refetchNotary();
    else if (companyData && !isEmpty(companyData)) refetchCompany();
  };

  const HeaderBelowComponent = useMemo(() => {
    if (notaryData) return NotaryInfoSummary;
    if (companyData) return ClientInfoSummary;
    return null;
  }, [notaryData, companyData, refetchDataWhenGenerateInvoice]);

  return (
    <AccountingPageContainer
      headerBelow={
        loading ? undefined : (
          <div>
            {HeaderBelowComponent && (
              <div className='accounting-detail-container'>
                <HeaderBelowComponent
                  refetchInvoice={refetchDataWhenGenerateInvoice}
                  userData={notaryData}
                  companyData={companyData}
                  className='header-section'
                />
              </div>
            )}
            <div className='accounting-page-container'>
              <div className='search-filter-container'>
                <Filter />
              </div>
            </div>
          </div>
        )
      }>
      {loading && <CircularProgress />}
      {!loading && (
        <div className='accounting-detail-container p-4 sm:p-0'>
          {!!companyData && (
            <ClientInvoiceTable
              refetchUserData={refetchDataWhenGenerateInvoice}
              loading={loading}
              companyData={companyData}
            />
          )}
          {!!notaryData && (
            <NotaryPaymentTable
              refetchUserData={refetchDataWhenGenerateInvoice}
              loading={loading}
              userData={notaryData}
            />
          )}
        </div>
      )}
    </AccountingPageContainer>
  );
};

export default React.memo(AccountingDetail);
